// 배치 시스템에서 생성되는 분리된 데이터 파일들을 동적으로 로드

import { normalizeExamKey, loadJmcdMappings, englishToKorean, getAlternativeNames } from './examKeyUnifier.js';
import { examData } from '../data/examData.js';

/**
 * 시험일정 관리 유틸리티
 * 배치 시스템에서 생성되는 분리된 데이터를 활용한 시험일정 조회 및 계산
 */

class ExamScheduleUtils {
  constructor() {
    this.scheduleData = null;
    this.today = new Date();
    this.loadPromise = this.loadScheduleData();
  }

  /**
   * examCode를 실제 데이터 키로 변환하는 통합 헬퍼 함수
   * CBT 제공 자격증과 비CBT 자격증을 통합 처리
   * jmcd 코드 기반 매핑 시스템 통합
   * @param {string} examCode - 영문 또는 한글 자격증 코드
   * @returns {Promise<string|null>} - 실제 데이터에서 사용되는 키 또는 null
   */
  async resolveExamKey(examCode) {
    if (!examCode || !this.scheduleData) return null;

    // 0차: 직접 매핑 테이블 (자주 실패하는 케이스)
    const directMappings = {
      'hvac-refrigeration-industrial-engineer': '냉동공조산업기사',
      'fisheries-management-engineer': '어업생산관리기사', 
      'marine-production-management-engineer': '어업생산관리기사',
      'agricultural-machinery-engineer': '농업기계기사',
      'environmental-engineer': '환경기사',
      'water-treatment-engineer': '수질환경기사',
      'gas-engineer': '가스기사',
      'welding-engineer': '용접기사',
      'painting-engineer': '도장기사',
      'refrigeration-technician': '냉동기능사',
      'hvac-technician': '공조냉동기계기능사'
    };

    if (directMappings[examCode]) {
      const mappedKey = directMappings[examCode];
      if (this.scheduleData.examSchedules[mappedKey]) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`직접매핑: "${examCode}" → "${mappedKey}"`);
        }
        return mappedKey;
      }
    }

    // 0.5차: examData title 직접 매칭 (CBT 자격증의 일정 키 == examData title; 괄호 분야명 등 정규화 누락 케이스 보강)
    const examDataTitle = examData && examData[examCode] && examData[examCode].title;
    if (examDataTitle && this.scheduleData.examSchedules[examDataTitle]) {
      return examDataTitle;
    }

    // 1차: 통합 키 정규화 시도 (CBT 매핑)
    const normalized = normalizeExamKey(examCode);
    if (normalized && this.scheduleData.examSchedules[normalized.key]) {
      if (process.env.NODE_ENV === 'development' && normalized.originalKey !== normalized.key) {
        console.log(`통합매핑: "${normalized.originalKey}" → "${normalized.key}" (CBT: ${normalized.isCBT})`);
      }
      return normalized.key;
    }

    // 2차: 원본 키로 직접 조회 (정규화 실패 시 폴백)
    if (this.scheduleData.examSchedules[examCode]) {
      return examCode;
    }

    // 3차: jmcd 기반 전체 자격증 매핑 시도
    try {
      const jmcdMappings = await loadJmcdMappings();
      
      // jmcd 매핑에서 직접 검색
      if (jmcdMappings[examCode]) {
        const mappedName = jmcdMappings[examCode];
        if (this.scheduleData.examSchedules[mappedName]) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`jmcd매핑: "${examCode}" → "${mappedName}"`);
          }
          return mappedName;
        }
      }

      // jmcd 매핑에서 스마트 부분 매칭 검색
      const smartMatches = [];
      for (const [jmcd, examName] of Object.entries(jmcdMappings)) {
        // 1. 완전 포함 매칭
        if (examName.includes(examCode) || examCode.includes(examName)) {
          smartMatches.push({ jmcd, examName, score: 10 });
        }
        // 2. 키워드 기반 매칭
        else if (examCode.length >= 3) {
          const codeWords = examCode.replace(/[^가-힣a-zA-Z]/g, ' ').split(/\s+/).filter(w => w.length >= 2);
          const nameWords = examName.replace(/[^가-힣a-zA-Z]/g, ' ').split(/\s+/).filter(w => w.length >= 2);
          
          let matchScore = 0;
          for (const codeWord of codeWords) {
            for (const nameWord of nameWords) {
              if (nameWord.includes(codeWord) || codeWord.includes(nameWord)) {
                matchScore += codeWord.length;
              }
            }
          }
          
          if (matchScore >= 3) {
            smartMatches.push({ jmcd, examName, score: matchScore });
          }
        }
      }

      // 최고 점수의 매치 선택
      if (smartMatches.length > 0) {
        const bestMatch = smartMatches.sort((a, b) => b.score - a.score)[0];
        if (this.scheduleData.examSchedules[bestMatch.examName]) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`jmcd스마트매핑: "${examCode}" → "${bestMatch.examName}" (jmcd: ${bestMatch.jmcd}, score: ${bestMatch.score})`);
          }
          return bestMatch.examName;
        }
      }
    } catch (error) {
      console.warn('jmcd 매핑 로드 실패:', error);
    }

    // 4차: 영문 용어 매핑 (기존 로직 유지)
    const scheduleKeys = Object.keys(this.scheduleData.examSchedules);
    
    if (examCode.includes('-')) {
      const termMapping = {
        'engineer': '기사',
        'technician': '기능사', 
        'industrial': '산업',
        'safety': '안전',
        'fire': '소방',
        'electrical': '전기',
        'mechanical': '기계',
        'general': '일반',
        'information': '정보',
        'processing': '처리',
        'computer': '컴퓨터',
        'construction': '시공',
        'civil': '토목',
        'chemical': '화공',
        'architectural': '건축',
        'air': '공조',
        'conditioning': '냉동',
        'refrigeration': '냉동',
        'hvac': '냉동공조',
        'agricultural': '농업',
        'machinery': '기계',
        'equipment': '설비',
        'management': '관리',
        'production': '생산',
        'fisheries': '어업',
        'marine': '어업',
        'environmental': '환경',
        'water': '수질',
        'treatment': '처리',
        'gas': '가스',
        'welding': '용접',
        'painting': '도장'
      };
      
      const searchTerms = examCode.split('-');
      const possibleMatches = scheduleKeys.filter(key => {
        return searchTerms.every(term => {
          const mappedTerm = termMapping[term];
          return mappedTerm && key.includes(mappedTerm);
        });
      });
      
      if (possibleMatches.length === 1) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`용어매핑: "${examCode}" → "${possibleMatches[0]}"`);
        }
        return possibleMatches[0];
      }
    }

    // 5차: 부분 문자열 매칭 (최후의 수단)
    if (examCode.length >= 3) {
      const partialMatches = scheduleKeys.filter(key => 
        key.includes(examCode) || examCode.includes(key)
      );
      
      if (partialMatches.length === 1) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`부분매칭: "${examCode}" → "${partialMatches[0]}"`);
        }
        return partialMatches[0];
      }
    }

    // 6차: 대체 매핑 시스템 (최후의 수단)
    try {
      const alternatives = await getAlternativeNames(examCode);
      if (alternatives.length > 0) {
        for (const altName of alternatives) {
          if (this.scheduleData.examSchedules[altName]) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`대체매핑: "${examCode}" → "${altName}"`);
            }
            return altName;
          }
        }
      }
    } catch (error) {
      console.warn('대체 매핑 실패:', error);
    }

    if (process.env.NODE_ENV === 'development') {
      console.warn(`매핑 실패: "${examCode}" (618개 자격증 중 매칭 불가)`);
    }
    return null;
  }

  /**
   * 배치 시스템에서 생성된 시험일정 데이터 로드
   * @returns {Promise<void>}
   */
  /**
   * 강력한 재시도 로직을 가진 데이터 로드 메소드
   * @param {string} url - 로드할 URL
   * @param {number} retries - 재시도 횟수 (기본 3회)
   * @param {number} timeout - 타임아웃 (기본 10초)
   * @returns {Promise<Response>}
   */
  async fetchWithRetry(url, retries = 3, timeout = 10000) {
    for (let i = 0; i <= retries; i++) {
      try {
        // AbortController로 타임아웃 제어
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        const response = await fetch(url, { 
          signal: controller.signal,
          cache: 'no-cache', // 캐시 문제 방지
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          return response;
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
      } catch (error) {
        const isLastAttempt = i === retries;
        
        if (error.name === 'AbortError') {
          console.warn(`데이터 로드 타임아웃 (시도 ${i + 1}/${retries + 1}):`, url);
        } else {
          console.warn(`데이터 로드 실패 (시도 ${i + 1}/${retries + 1}):`, error.message);
        }
        
        if (isLastAttempt) {
          throw new Error(`${retries + 1}회 시도 후 로드 실패: ${error.message}`);
        }
        
        // 재시도 전 대기 (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, i), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  async loadScheduleData() {
    this.loadStatus = {
      status: 'loading',
      error: null,
      lastAttempt: new Date().toISOString(),
      retries: 0
    };
    
    try {
      // 1차: 최신 배치 데이터 로드 시도
      console.log('📊 시험일정 데이터 로드 시작...');
      const response = await this.fetchWithRetry('/qnet-exam-schedules.json');
      const data = await response.json();
      
      // 데이터 무결성 검증
      if (!data.examSchedules || typeof data.examSchedules !== 'object') {
        throw new Error('데이터 구조가 올바르지 않습니다');
      }
      
      const scheduleCount = Object.keys(data.examSchedules).length;
      if (scheduleCount === 0) {
        throw new Error('빈 데이터가 로드되었습니다');
      }

      // 캐시 무효화 검증
      const cachedVersion = localStorage.getItem('examSchedules_version');
      const currentVersion = data.dataVersion || data.lastUpdated || new Date().toISOString();
      
      // 버전이 다르면 캐시된 데이터 모두 클리어
      if (cachedVersion && cachedVersion !== currentVersion) {
        console.log('🔄 데이터 버전 변경 감지, 캐시 클리어:', cachedVersion, '→', currentVersion);
        localStorage.removeItem('examSchedules_backup');
        localStorage.removeItem('examSchedules_cache');
      }
      
      // 현재 버전 저장
      localStorage.setItem('examSchedules_version', currentVersion);
      
      this.scheduleData = data;
      this.loadStatus = {
        status: 'success',
        error: null,
        lastAttempt: new Date().toISOString(),
        dataSource: 'primary',
        scheduleCount
      };
      
      // 성공 시 로컬 스토리지에 백업 저장
      try {
        localStorage.setItem('examSchedules_backup', JSON.stringify({
          data,
          cachedAt: new Date().toISOString()
        }));
      } catch (storageError) {
        console.warn('로컬 스토리지 백업 저장 실패:', storageError);
      }
      
      console.log(`✅ 배치 데이터 로드 성공: ${scheduleCount}개 자격증, 업데이트: ${data.lastUpdated}`);
      return;
      
    } catch (primaryError) {
      console.warn('⚠️ 1차 데이터 로드 실패:', primaryError.message);
      
      try {
        // 2차: 로컬 스토리지 백업 시도
        const cachedData = localStorage.getItem('examSchedules_backup');
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          const cacheAge = Date.now() - new Date(parsedData.cachedAt).getTime();
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7일
          
          if (cacheAge < maxAge) {
            this.scheduleData = parsedData.data;
            this.loadStatus = {
              status: 'success',
              error: null,
              lastAttempt: new Date().toISOString(),
              dataSource: 'localStorage',
              scheduleCount: Object.keys(parsedData.data.examSchedules).length,
              cacheAge: Math.floor(cacheAge / (1000 * 60 * 60))
            };
            
            console.log(`📱 로컬 스토리지 백업 사용: ${Math.floor(cacheAge / (1000 * 60 * 60))}시간 전 데이터`);
            return;
          }
        }
      } catch (cacheError) {
        console.warn('📱 로컬 스토리지 백업 실패:', cacheError.message);
      }
      
      try {
        // 3차: 정적 폴백 데이터 로드
        console.log('🔄 정적 폴백 데이터 로드 시도...');
        const fallbackData = await import('../data/examSchedules.json');
        
        this.scheduleData = fallbackData.default;
        this.loadStatus = {
          status: 'success',
          error: `1차 로드 실패: ${primaryError.message}`,
          lastAttempt: new Date().toISOString(),
          dataSource: 'fallback',
          scheduleCount: Object.keys(this.scheduleData.examSchedules || {}).length
        };
        
        console.log('🆘 정적 폴백 데이터 사용');
        return;
        
      } catch (fallbackError) {
        // 완전 실패 - 빈 데이터로 초기화하되 상세한 에러 정보 제공
        this.scheduleData = {
          examSchedules: {},
          lastUpdated: new Date().toISOString(),
          source: '빈 데이터 (로드 실패)',
          updateFrequency: 'failed'
        };
        
        this.loadStatus = {
          status: 'error',
          error: `모든 데이터 로드 실패: ${primaryError.message}`,
          lastAttempt: new Date().toISOString(),
          dataSource: 'none',
          scheduleCount: 0,
          troubleshooting: [
            '브라우저 캐시를 클리어해주세요',
            '네트워크 연결을 확인해주세요',
            '페이지를 새로고침해주세요',
            '다른 브라우저로 시도해보세요'
          ]
        };
        
        console.error('❌ 모든 데이터 로드 실패:', {
          primaryError: primaryError.message,
          fallbackError: fallbackError.message
        });
      }
    }
  }

  /**
   * 데이터 로드 완료를 보장하는 헬퍼 메소드
   * @returns {Promise<void>}
   */
  async ensureDataLoaded() {
    if (!this.scheduleData) {
      await this.loadPromise;
    }
  }

  /**
   * 현재 로드 상태 반환
   * @returns {Object} - 로드 상태 정보
   */
  getLoadStatus() {
    return this.loadStatus || {
      status: 'unknown',
      error: null,
      lastAttempt: null,
      dataSource: 'unknown',
      scheduleCount: 0
    };
  }

  /**
   * 브라우저 캐시 감지 및 무효화
   * @returns {Promise<boolean>} - 캐시 문제가 감지되었는지 여부
   */
  async detectAndClearBrowserCache() {
    try {
      // 1차: 간단한 HEAD 요청으로 서버 응답 확인
      const headResponse = await fetch('/qnet-exam-schedules.json', { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      // 2차: 타임스탬프 추가로 캐시 무효화 시도
      const timestamp = Date.now();
      const response = await fetch(`/qnet-exam-schedules.json?t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`캐시 감지 실패: ${response.status}`);
      }

      const data = await response.json();
      
      // 3차: 로컬 스토리지 버전과 비교
      const cachedVersion = localStorage.getItem('examSchedules_version');
      const serverVersion = data.dataVersion || data.lastUpdated;
      
      if (cachedVersion && serverVersion && cachedVersion !== serverVersion) {
        console.log('🔄 서버 버전 업데이트 감지, 브라우저 캐시 무효화 필요');
        
        // Service Worker 캐시 무효화 시도
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            if (registration.active) {
              registration.active.postMessage({ command: 'clearCache' });
            }
          } catch (swError) {
            console.warn('Service Worker 캐시 클리어 실패:', swError);
          }
        }
        
        return true; // 캐시 문제 감지됨
      }
      
      return false; // 캐시 문제 없음
      
    } catch (error) {
      console.warn('브라우저 캐시 감지 실패:', error);
      return false;
    }
  }

  /**
   * 데이터 재로드 강제 실행
   * @returns {Promise<void>}
   */
  async forceReload() {
    console.log('🔄 데이터 강제 재로드 시작...');
    
    // 브라우저 캐시 감지 및 무효화
    await this.detectAndClearBrowserCache();
    
    // 로컬 스토리지 캐시도 클리어
    localStorage.removeItem('examSchedules_backup');
    localStorage.removeItem('examSchedules_cache');
    localStorage.removeItem('examSchedules_version');
    
    this.scheduleData = null;
    this.loadPromise = this.loadScheduleData();
    await this.loadPromise;
  }

  /**
   * 날짜 문자열을 Date 객체로 변환
   * @param {string} dateStr - YYYY-MM-DD 형식의 날짜 문자열
   * @returns {Date|null}
   */
  parseDate(dateStr) {
    if (!dateStr || dateStr === '없음') return null;
    
    // 2025-01-06 형식 처리
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(dateStr);
    }
    
    // 특수 형식 처리 (예: "1.31(수)부터") - 연도는 현재연도 기준
    const match = dateStr.match(/(\d{1,2})\.(\d{1,2})/);
    if (match) {
      const [, month, day] = match;
      return new Date(new Date().getFullYear(), parseInt(month) - 1, parseInt(day));
    }
    
    return null;
  }

  /**
   * D-day 계산
   * @param {Date} targetDate - 목표 날짜
   * @returns {number} - 양수: 남은 일수, 음수: 지난 일수, 0: 오늘
   */
  calculateDday(targetDate) {
    if (!targetDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    
    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  /**
   * 접수 상태 계산
   * @param {string} applicationStart - 접수 시작일 (YYYY-MM-DD)
   * @param {string} applicationEnd - 접수 종료일 (YYYY-MM-DD)
   * @returns {object} - 상태 정보
   */
  getApplicationStatus(applicationStart, applicationEnd) {
    const startDate = this.parseDate(applicationStart);
    const endDate = this.parseDate(applicationEnd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!startDate || !endDate) {
      return {
        status: 'unknown',
        message: '접수 정보 없음',
        daysRemaining: null
      };
    }

    if (today < startDate) {
      const daysUntilStart = this.calculateDday(startDate);
      return {
        status: 'upcoming',
        message: `접수 예정 (${daysUntilStart}일 후)`,
        daysRemaining: daysUntilStart
      };
    } else if (today >= startDate && today <= endDate) {
      const daysUntilEnd = this.calculateDday(endDate);
      return {
        status: 'open',
        message: `접수 중 (${daysUntilEnd}일 남음)`,
        daysRemaining: daysUntilEnd
      };
    } else {
      return {
        status: 'closed',
        message: '접수 마감',
        daysRemaining: 0
      };
    }
  }

  /**
   * 특정 자격증의 다가오는 시험일정 조회
   * @param {string} examCode - 자격증 코드 (영문 또는 한글)
   * @param {number} limit - 반환할 최대 일정 수 (기본값: 3)
   * @returns {Promise<Array>} - 다가오는 시험일정 목록
   */
  async getUpcomingSchedules(examCode, limit = 3) {
    await this.ensureDataLoaded();
    
    if (!examCode) {
      console.warn('examCode가 제공되지 않았습니다.');
      return [];
    }
    
    const resolvedKey = await this.resolveExamKey(examCode);
    if (!resolvedKey) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`시험일정을 찾을 수 없습니다: ${examCode}`);
      }
      return [];
    }

    const examInfo = this.scheduleData.examSchedules[resolvedKey];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 유효한 시험일정만 필터링하고 정렬
    const upcomingSchedules = examInfo.schedules
      .map(schedule => {
        const examDate = this.parseDate(schedule.examDate);
        if (!examDate) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('날짜 파싱 실패:', schedule.examDate, schedule);
          }
          return null;
        }

        // 잘못된 데이터 필터링 (시행종목 목록이 들어간 경우)
        const isValidSchedule = this.isValidSchedule(schedule);
        if (!isValidSchedule) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('유효하지 않은 일정:', schedule);
          }
          return null;
        }

        const processedSchedule = {
          ...schedule,
          examDateObj: examDate,
          dday: this.calculateDday(examDate),
          applicationStatus: this.getApplicationStatus(
            schedule.applicationStart, 
            schedule.applicationEnd
          )
        };
        
        if (process.env.NODE_ENV === 'development') {
          console.log('처리된 일정:', processedSchedule.round, 
            '시험일:', examDate, 
            'D-day:', processedSchedule.dday,
            '오늘과 비교:', examDate >= today ? '미래' : '과거');
        }
        
        return processedSchedule;
      })
      .filter(schedule => {
        if (!schedule) return false;
        const isFuture = schedule.examDateObj >= today;
        if (process.env.NODE_ENV === 'development') {
          console.log('날짜 필터링:', schedule.round, isFuture ? '유지' : '제거');
        }
        return isFuture;
      })
      .sort((a, b) => a.examDateObj - b.examDateObj)
      .slice(0, limit);

    return upcomingSchedules;
  }

  /**
   * 시험일정 데이터 유효성 검사
   * @param {object} schedule - 시험일정 객체
   * @returns {boolean} - 유효한 데이터 여부
   */
  isValidSchedule(schedule) {
    if (process.env.NODE_ENV === 'development') {
      console.log('유효성 검사:', schedule);
    }
    
    // 시행종목 목록이 들어간 잘못된 데이터 필터링
    if (schedule.examDate && schedule.examDate.includes('디자인 :')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('필터링: 디자인 포함 데이터');
      }
      return false;
    }
    
    if (schedule.applicationPeriod && schedule.applicationPeriod === '시행종목') {
      if (process.env.NODE_ENV === 'development') {
        console.log('필터링: 시행종목 데이터');
      }
      return false;
    }
    
    if (schedule.round && schedule.round.includes('필기시험이 없는')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('필터링: 필기시험 없음');
      }
      return false;
    }
    
    // 정상적인 회차 정보가 있는지 확인
    if (!schedule.round || (!schedule.round.includes('회') && !schedule.round.includes('년'))) {
      if (process.env.NODE_ENV === 'development') {
        console.log('필터링: 회차 정보 없음', schedule.round);
      }
      return false;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ 유효한 일정:', schedule.round, schedule.examDate);
    }
    
    return true;
  }

  /**
   * 가장 가까운 시험일정 조회 (미래 우선, 없으면 최근 과거)
   * @param {string} examCode - 자격증 코드 (영문 또는 한글)
   * @returns {Promise<object|null>} - 가장 가까운 시험일정
   */
  async getNextSchedule(examCode) {
    await this.ensureDataLoaded();
    
    const resolvedKey = await this.resolveExamKey(examCode);
    if (!resolvedKey) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`시험일정을 찾을 수 없습니다: ${examCode}`);
      }
      return null;
    }
    
    // 먼저 미래 일정 조회
    const upcomingSchedules = await this.getUpcomingSchedules(resolvedKey, 1);
    if (upcomingSchedules.length > 0) {
      return upcomingSchedules[0];
    }
    
    // 미래 일정이 없다면 최근 과거 일정 조회
    const recentSchedule = await this.getMostRecentPastSchedule(resolvedKey);
    return recentSchedule;
  }
  
  /**
   * 가장 최근 과거 시험일정 조회
   * @param {string} examCode - 자격증 코드 (영문 또는 한글)
   * @returns {Promise<object|null>} - 가장 최근 과거 시험일정
   */
  async getMostRecentPastSchedule(examCode) {
    await this.ensureDataLoaded();
    
    if (!examCode) {
      return null;
    }
    
    const resolvedKey = await this.resolveExamKey(examCode);
    if (!resolvedKey) {
      return null;
    }

    const examInfo = this.scheduleData.examSchedules[resolvedKey];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 과거 시험일정만 필터링하고 가장 최근 것 선택
    const pastSchedules = examInfo.schedules
      .map(schedule => {
        const examDate = this.parseDate(schedule.examDate);
        if (!examDate) return null;

        const isValidSchedule = this.isValidSchedule(schedule);
        if (!isValidSchedule) return null;

        return {
          ...schedule,
          examDateObj: examDate,
          dday: this.calculateDday(examDate),
          applicationStatus: {
            status: 'closed',
            message: '접수 마감',
            daysRemaining: 0
          },
          isPastSchedule: true // 과거 일정임을 표시
        };
      })
      .filter(schedule => schedule && schedule.examDateObj < today)
      .sort((a, b) => b.examDateObj - a.examDateObj); // 최근 것부터 정렬

    if (process.env.NODE_ENV === 'development' && pastSchedules.length > 0) {
      console.log('과거 일정 중 가장 최근:', pastSchedules[0].round, pastSchedules[0].examDate);
    }

    return pastSchedules.length > 0 ? pastSchedules[0] : null;
  }

  /**
   * 자격증 정보 조회
   * @param {string} examCode - 자격증 코드 (영문 또는 한글)
   * @returns {Promise<object|null>} - 자격증 기본 정보
   */
  async getExamInfo(examCode) {
    await this.ensureDataLoaded();
    
    const resolvedKey = await this.resolveExamKey(examCode);
    if (!resolvedKey) return null;

    const examInfo = this.scheduleData.examSchedules[resolvedKey];
    
    return {
      code: resolvedKey,
      korean: examInfo.examName || englishToKorean(examCode) || examCode,
      category: examInfo.category,
      totalSchedules: examInfo.schedules.length
    };
  }

  /**
   * 현재 연도의 모든 시험일정 조회 (다중 분야 지원)
   * @param {string} examCode - 자격증 코드 (영문 또는 한글)
   * @returns {Promise<Array>} - 현재 연도의 모든 시험일정
   */
  async getCurrentYearSchedules(examCode) {
    await this.ensureDataLoaded();
    
    if (!examCode) {
      console.warn('examCode가 제공되지 않았습니다.');
      return [];
    }
    
    const resolvedKey = await this.resolveExamKey(examCode);
    if (!resolvedKey) {
      // 대체 매핑으로 다중 분야 자격증 처리
      try {
        const alternatives = await getAlternativeNames(examCode);
        if (alternatives.length > 0) {
          const allSchedules = [];
          
          for (const altName of alternatives) {
            if (this.scheduleData.examSchedules[altName]) {
              const altSchedules = await this.getCurrentYearSchedulesByExactName(altName);
              // 분야 정보 추가
              const schedulesWithField = altSchedules.map(schedule => ({
                ...schedule,
                examField: altName.includes('(') ? altName.match(/\(([^)]+)\)/)?.[1] : null,
                originalExamName: altName
              }));
              allSchedules.push(...schedulesWithField);
            }
          }
          
          if (allSchedules.length > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`다중분야매핑: "${examCode}" → ${alternatives.length}개 분야`);
            }
            return allSchedules;
          }
        }
      } catch (error) {
        console.warn('다중 분야 매핑 실패:', error);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.warn(`시험일정을 찾을 수 없습니다: ${examCode}`);
        console.log('사용 가능한 키들:', Object.keys(this.scheduleData.examSchedules));
      }
      return [];
    }

    return this.getCurrentYearSchedulesByExactName(resolvedKey);
  }

  /**
   * 정확한 자격증명으로 현재 연도 시험일정 조회 (내부 헬퍼)
   * @param {string} exactExamName - 정확한 자격증명
   * @returns {Promise<Array>} - 현재 연도의 모든 시험일정
   */
  async getCurrentYearSchedulesByExactName(exactExamName) {
    const examInfo = this.scheduleData.examSchedules[exactExamName];
    if (!examInfo) {
      return [];
    }

    const currentYear = new Date().getFullYear();
    
    // 현재 연도의 유효한 시험일정만 필터링
    const currentYearSchedules = examInfo.schedules
      .filter(schedule => {
        // 유효한 일정인지 확인
        const isValidSchedule = this.isValidSchedule(schedule);
        if (!isValidSchedule) return false;
        
        // 현재 연도 일정인지 확인
        const examDate = this.parseDate(schedule.examDate);
        if (!examDate) return false;
        
        return examDate.getFullYear() === currentYear;
      })
      .map(schedule => {
        const examDate = this.parseDate(schedule.examDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return {
          ...schedule,
          examDateObj: examDate,
          dday: this.calculateDday(examDate),
          applicationStatus: this.getApplicationStatus(
            schedule.applicationStart, 
            schedule.applicationEnd
          ),
          isPast: examDate < today,
          isFuture: examDate >= today
        };
      })
      .sort((a, b) => a.examDateObj - b.examDateObj); // 날짜순 정렬

    return currentYearSchedules;
  }

  /**
   * 모든 자격증의 시험일정 요약
   * @returns {Promise<Array>} - 모든 자격증의 다음 시험일정
   */
  async getAllUpcomingSchedules() {
    await this.ensureDataLoaded();
    
    const allSchedules = [];

    for (const examCode of Object.keys(this.scheduleData.examSchedules)) {
      const nextSchedule = await this.getNextSchedule(examCode);
      if (nextSchedule) {
        const examInfo = await this.getExamInfo(examCode);
        allSchedules.push({
          ...examInfo,
          nextSchedule
        });
      }
    }

    // D-day 순으로 정렬
    return allSchedules.sort((a, b) => a.nextSchedule.dday - b.nextSchedule.dday);
  }

  /**
   * D-day 텍스트 포맷팅
   * @param {number} dday - D-day 값
   * @returns {string} - 포맷된 D-day 텍스트
   */
  formatDday(dday) {
    if (dday === null) return '';
    if (dday === 0) return 'D-DAY';
    if (dday > 0) return `D-${dday}`;
    return `D+${Math.abs(dday)}`;
  }

  /**
   * 날짜 포맷팅 (한국어)
   * @param {Date} date - 날짜 객체
   * @returns {string} - 포맷된 날짜 문자열
   */
  formatDate(date) {
    if (!date) return '';
    
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'short'
    };
    
    return date.toLocaleDateString('ko-KR', options);
  }

  /**
   * 시험일정 데이터의 마지막 업데이트 일시
   * @returns {Promise<string>} - 업데이트 날짜
   */
  async getLastUpdated() {
    await this.ensureDataLoaded();
    return this.scheduleData.lastUpdated;
  }

  /**
   * 시험일정 데이터 소스 정보
   * @returns {Promise<object>} - 데이터 소스 정보
   */
  async getDataSource() {
    await this.ensureDataLoaded();
    return {
      source: this.scheduleData.source || 'Q-Net API (배치 시스템)',
      version: this.scheduleData.version,
      lastUpdated: this.scheduleData.lastUpdated,
      scrapedAt: this.scheduleData.scrapedAt,
      updateFrequency: this.scheduleData.updateFrequency || 'daily'
    };
  }
}

// 싱글톤 인스턴스 생성
const examScheduleUtils = new ExamScheduleUtils();

// 편의 함수들 내보내기
export const getNextExamSchedule = (examCode) => 
  examScheduleUtils.getNextSchedule(examCode);

export const getUpcomingExamSchedules = (examCode, limit = 3) => 
  examScheduleUtils.getUpcomingSchedules(examCode, limit);

export const getAllUpcomingExams = () => 
  examScheduleUtils.getAllUpcomingSchedules();

export const formatDday = (dday) => 
  examScheduleUtils.formatDday(dday);

export const formatDate = (date) => 
  examScheduleUtils.formatDate(date);

export const getExamInfo = (examCode) => 
  examScheduleUtils.getExamInfo(examCode);

export const getCurrentYearSchedules = (examCode) => 
  examScheduleUtils.getCurrentYearSchedules(examCode);

export const getScheduleDataSource = () => 
  examScheduleUtils.getDataSource();

export const getLoadStatus = () =>
  examScheduleUtils.getLoadStatus();

export const forceReloadScheduleData = () =>
  examScheduleUtils.forceReload();

export default examScheduleUtils;